import { CommercialReuseDialog } from "../components/CommercialReuseDialog";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  type SelectChangeEvent,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Label } from "@/components/Label";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getPrefectureMenuItems,
  getPrefectureNameById,
} from "@/utils/utilsAcquireBook";
import { useValidator } from "@/features/commercialBook/hooks/useValidator";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { SizingWrapper, SizingWrapperStyle } from "@/components/Wrapper";
import { Button, ButtonVariantOption } from "@/components/Button";
import { useCity } from "@/hooks/useCity";
import { type CityListResponse } from "@/types/city";
import {
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useCorporateMasterEnabled } from "@/hooks/useCorporateMasterEnabled";
import type { FetchParams } from "@/features/commercialBook/types";

export const CommercialBook: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const SectionWrapper = isMobile ? Box : SectionPaper;
  const compactSx = isMobile
    ? {
        "& .MuiTypography-h4": { fontSize: "1.4rem" },
        "& .MuiTypography-h5": { fontSize: "1.25rem" },
        "& .MuiTypography-h6": { fontSize: "1.1rem" },
        "& .MuiTypography-subtitle1": { fontSize: "1rem" },
        "& .MuiTypography-body1": { fontSize: "0.95rem" },
        "& .MuiTypography-body2": { fontSize: "0.9rem" },
        "& .MuiTypography-caption": { fontSize: "0.78rem" },
        "& .MuiButton-root": { fontSize: "0.9rem", py: 0.4 },
        "& .MuiChip-root": { height: 24, fontSize: "0.8rem" },
        "& .MuiInputBase-input": { fontSize: "0.95rem" },
        "& .MuiInputLabel-root": { fontSize: "0.95rem" },
        "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
      }
    : undefined;
  const [searchParams] = useSearchParams();
  const corporateMasterEnabled = useCorporateMasterEnabled();
  const isReuseLink = searchParams.get("reuse") === "1";
  // 法人マスタ（リユース）が無効なときのリユース導線は、通常の新規取得に会社名・番号を引き継がない
  // （リユースのつもりで通常料金の取得へ進ませないため）
  const ignoreInitialParams = isReuseLink && !corporateMasterEnabled;

  // クエリパラメータから初期値を取得
  const initialCompanyName = ignoreInitialParams
    ? ""
    : searchParams.get("companyName") ?? "";
  const initialCorporateNumber = ignoreInitialParams
    ? ""
    : searchParams.get("corporateNumber") ?? "";
  const initialPrefecturesId = ignoreInitialParams
    ? ""
    : searchParams.get("prefecturesId") ?? "";

  // 会社法人等番号を4桁-2桁-6桁に分割する関数
  const splitCorporateNumber = (
    corporateNumber: string | undefined
  ): [string, string, string] => {
    if (!corporateNumber) return ["", "", ""];
    // 数字のみを抽出（ハイフンなど除去）
    const digits = corporateNumber.replace(/[^0-9０-９]/g, "");
    if (digits.length !== 12) return ["", "", ""];
    return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 12)];
  };

  const [initialPart1, initialPart2, initialPart3] = splitCorporateNumber(
    initialCorporateNumber
  );

  // 都道府県セレクトボックス（MenuItemのvalueは数値、未選択時は空文字列）
  const [prefecture, setPrefecture] = useState<number | "">("");

  // 商号・名称テキストボックス
  const [commercialBookName, setStateCommercialBookName] = useState<string>("");

  // 会社法人等番号テキストボックス
  const [commercialBookNumberPart1, setCommercialBookNumberPart1] =
    useState<string>("");
  const [commercialBookNumberPart2, setCommercialBookNumberPart2] =
    useState<string>("");
  const [commercialBookNumberPart3, setCommercialBookNumberPart3] =
    useState<string>("");

  // クエリパラメータから初期値を設定
  useEffect(() => {
    if (initialPrefecturesId) {
      setPrefecture(Number(initialPrefecturesId));
    }
    if (initialCompanyName) {
      setStateCommercialBookName(initialCompanyName);
    }
    if (initialPart1 && initialPart2 && initialPart3) {
      setCommercialBookNumberPart1(initialPart1);
      setCommercialBookNumberPart2(initialPart2);
      setCommercialBookNumberPart3(initialPart3);
    }
  }, [
    initialPrefecturesId,
    initialCompanyName,
    initialPart1,
    initialPart2,
    initialPart3,
  ]);

  // 市区町村セレクトボックス
  const { city, setCity, cityList } = useCity(String(prefecture), "");

  // 検索方法の初期値 「商号・名称」か「会社法人番号」
  let defaultSearchType: "name" | "number";

  // 会社法人番号がある場合は会社法人番号で検索する
  if (initialCorporateNumber) {
    defaultSearchType = "number";
  } else {
    defaultSearchType = "name";
  }

  // 検索方法の選択状態
  const [commercialBookTypeCompanyName, setCommercialBookTypeCompanyName] =
    useState<boolean>(defaultSearchType === "name");
  const [commercialBookTypeCompanyNumber, setCommercialBookTypeCompanyNumber] =
    useState<boolean>(!(defaultSearchType === "name"));

  // 画面の表示中に法人マスタが無効になった場合も、リユース導線から入った会社名・番号を残さない
  useEffect(() => {
    if (!ignoreInitialParams) return;
    setPrefecture("");
    setStateCommercialBookName("");
    setCommercialBookNumberPart1("");
    setCommercialBookNumberPart2("");
    setCommercialBookNumberPart3("");
    setCommercialBookTypeCompanyName(true);
    setCommercialBookTypeCompanyNumber(false);
  }, [ignoreInitialParams]);

  // 本支店・事務所の選択（デフォルト: 全国）
  const [searchArea, setSearchArea] = useState<"city" | "country">("country");

  // 前方一致/完全一致
  const [startsWithMatch, setStartsWithMatch] = useState<boolean>(true);

  const validator = useValidator({
    commercialBookTypeCompanyName,
    commercialBookTypeCompanyNumber,
    searchArea,
  });

  const navigate = useNavigate();

  const { mypageTop } = useFeatureFlags();

  // 都道府県セレクトボックス変更時の処理
  const handlePrefecture = (event: SelectChangeEvent<number | "">): void => {
    const value = event.target.value as number | "";
    setPrefecture(value);
    validator.pref.validate(value);
    // 市区町村selectBoxの中身が変わるのでselectBoxを初期状態に戻す
    setCity("");
  };

  // 市区町村セレクトボックス変更時の処理
  const handleCity = (event: SelectChangeEvent<string>): void => {
    setCity(event.target.value);
    validator.city.validate(event.target.value);
  };

  // 市区町村のセレクトボックスの選択項目を生成する関数
  const getCityMenuItems = (
    cityListResponse: CityListResponse
  ): React.ReactNode | undefined => {
    const cityList = cityListResponse.list;
    if (cityList.length > 0) {
      return cityList.map((value) => (
        <MenuItem key={value.id} value={value.cityCode}>
          {value.name}
        </MenuItem>
      ));
    }
  };

  // 市区町村コードから市区町村名に変換する
  // 変換できない場合はundefined
  const getCityNameByCityCode = (code: string): string | undefined => {
    return cityList.list.find((city) => city.cityCode === code)?.name;
  };

  /**
   * バリデーションチェックを実行し、すべてのバリデーションが通過した場合にナビゲーションを行う
   *
   * - 検索エリアが全国の場合は都道府県・市区町村のバリデーションをスキップ
   * - 会社法人等番号での検索の場合は商号・名称のエラーをクリアし、番号のバリデーションを実行
   * - 商号・名称での検索の場合は会社法人等番号のエラーをクリアし、名称のバリデーションを実行
   * - すべてのバリデーション結果がtrueの場合のみナビゲーションを実行
   */
  const handleCheckValidation = (): void => {
    let results =
      searchArea === "country"
        ? []
        : [validator.pref.validate(prefecture), validator.city.validate(city)];

    if (commercialBookTypeCompanyNumber) {
      // 商号・名称のバリデーションエラーをクリア
      validator.commercialBookName.clearError();
      // 会社法人等番号のバリデーションを実行する
      results = results.concat([
        validator.commercialBookNumberPart1.validate(commercialBookNumberPart1),
        validator.commercialBookNumberPart2.validate(commercialBookNumberPart2),
        validator.commercialBookNumberPart3.validate(commercialBookNumberPart3),
      ]);
    } else if (commercialBookTypeCompanyName) {
      // 会社法人等番号のバリデーションエラーをクリア
      validator.commercialBookNumberPart1.clearError();
      validator.commercialBookNumberPart2.clearError();
      validator.commercialBookNumberPart3.clearError();
      // 商号・名称のバリデーションを実行する
      results = results.concat([
        validator.commercialBookName.validate(commercialBookName),
      ]);
    }

    // resultsの全ての値がtrueならvalidation通過とみなす
    if (results.every((value) => value)) {
      handleNavigate();
    }
  };

  // 会社・法人一覧に遷移
  const handleNavigate = (): void => {
    const prefectureName =
      searchArea === "country"
        ? "全国"
        : getPrefectureNameById(Number(prefecture));
    if (!prefectureName) {
      toast.error(
        "都道府県が不正です。再度同じ操作を行い、再発する場合はお問い合わせください。"
      );
      return;
    }

    const location =
      searchArea === "country" ? "なし" : getCityNameByCityCode(city);
    if (!location) {
      toast.error(
        "市区町村が不正です。再度同じ操作を行い、再発する場合はお問い合わせください。"
      );
      return;
    }

    const state: FetchParams = {
      prefectureName,
      location,
      matchMethod: commercialBookTypeCompanyName
        ? "商号・名称"
        : "会社法人等番号",
      matchType: startsWithMatch ? "前方一致" : "完全一致",
      closed: false,
      kubun: "商業及び法人",
      companyName: commercialBookTypeCompanyName
        ? commercialBookName
        : undefined,
      corporateNumber: commercialBookTypeCompanyName
        ? undefined
        : commercialBookNumberPart1 +
          commercialBookNumberPart2 +
          commercialBookNumberPart3,
    };

    navigate("/commercialbook/companylist", {
      state,
      replace: false,
    });
  };

  // 検索方法の変更時の処理
  const handleSearchTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const isCompanyName = event.target.value === "companyName";
    setCommercialBookTypeCompanyName(isCompanyName);
    setCommercialBookTypeCompanyNumber(!isCompanyName);

    if (isCompanyName) {
      setCommercialBookNumberPart1("");
      setCommercialBookNumberPart2("");
      setCommercialBookNumberPart3("");
      validator.commercialBookNumberPart1.clearError();
      validator.commercialBookNumberPart2.clearError();
      validator.commercialBookNumberPart3.clearError();

      return;
    }

    setStateCommercialBookName("");
    validator.commercialBookName.clearError();
  };

  // 本支店・事務所の変更時の処理
  const handleSearchArea = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchArea(
      event.target.value === "city" || event.target.value === "country"
        ? event.target.value
        : "city"
    );
  };

  // 完全一致/前方一致チェックボックスの変更時の処理
  const handleSearchConditionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const isExactMatch = event.target.value === "exactMatch";
    setStartsWithMatch(!isExactMatch);
  };

  // 本支店・事務所の変更時に都道府県・市区町村をリセット（初回マウント時はスキップ）
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPrefecture("");
    setCity("");
    validator.pref.clearError();
    validator.city.clearError();
    if (commercialBookName) {
      validator.commercialBookName.validate(commercialBookName);
    } else {
      validator.commercialBookName.clearError();
    }
  }, [searchArea]);

  if (isReuseLink && corporateMasterEnabled) {
    return (
      <CommercialReuseDialog
        key={initialCorporateNumber}
        corporateNumber={initialCorporateNumber}
        companyName={initialCompanyName}
        returnCorporateNumber={searchParams.get("returnCorporateNumber") ?? ""}
      />
    );
  }

  return (
    <PagePaper>
      <Box component="form" sx={compactSx}>
        {/*
          SP（md未満）ではタイトルと注意書きを縦積みにする。PCでは横並びを維持。
          タイトルは minWidth:0 を与え、狭幅で1文字ずつ縦に折れるのを防ぐ。
        */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          {/* ページタイトル */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: { xs: 1, md: 3 },
              minWidth: 0,
            }}
          >
            <RequestPageIcon sx={{ mr: 1, flexShrink: 0 }} fontSize="large" />
            <Box sx={{ minWidth: 0 }}>
              <PageTitle>法人名から商業登記簿を取得する</PageTitle>
            </Box>
          </Box>
          <Box
            sx={{
              ml: { xs: 0, md: 10 },
              mb: { xs: 2, md: 3 },
              "& ul": { pl: 3, my: 0 },
              "& li": { fontSize: { xs: "0.8rem", md: "inherit" } },
            }}
          >
            <ul>
              <li>最新商業登記簿（閉鎖登記含む）を取得いたします</li>
              <li>
                市区町村検索の場合は該当する会社・法人等が100件未満、全国検索の場合は300件未満になるよう検索してください
              </li>
            </ul>
          </Box>
        </Box>
        <SectionWrapper>
          {/* 検索方法 */}
          <Box sx={{ m: isMobile ? 0 : 2 }}>
            <Label>検索方法</Label>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="search-method"
                name="search-method"
                value={
                  commercialBookTypeCompanyNumber
                    ? "companyNumber"
                    : "companyName"
                }
                onChange={handleSearchTypeChange}
              >
                <FormControlLabel
                  value="companyName"
                  control={<Radio />}
                  label="商号・名称"
                />
                <FormControlLabel
                  value="companyNumber"
                  control={<Radio />}
                  label="会社法人等番号"
                />
              </RadioGroup>
            </FormControl>

            {/* 都道府県、市区町村、区分 */}
            <Box sx={{ my: 2 }}>
              <Label>本支店・事務所</Label>
            </Box>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="search-area"
                name="search-area"
                value={searchArea}
                onChange={handleSearchArea}
              >
                <FormControlLabel
                  value="city"
                  control={<Radio />}
                  label="市区町村"
                />
                <FormControlLabel
                  value="country"
                  control={<Radio />}
                  label="全国"
                />
              </RadioGroup>
            </FormControl>
            {searchArea === "country" ? null : (
              <Grid container spacing={2}>
                <Grid item>
                  <Box sx={{ my: 1 }}>
                    <Label required={true}>都道府県</Label>
                    <SizingWrapper>
                      <FormControl fullWidth error={validator.pref.state.error}>
                        <InputLabel id="select-label">都道府県</InputLabel>
                        <Select<number | "">
                          labelId="select-label"
                          label="都道府県"
                          value={prefecture}
                          onChange={handlePrefecture}
                        >
                          {getPrefectureMenuItems()}
                        </Select>
                        <FormHelperText>
                          {validator.pref.state.helperTextWhenError}
                        </FormHelperText>
                      </FormControl>
                    </SizingWrapper>
                  </Box>
                </Grid>
                <Grid item>
                  <Box sx={{ my: 1 }}>
                    <Label required={true}>市区町村</Label>
                    <SizingWrapper>
                      <FormControl fullWidth error={validator.city.state.error}>
                        <InputLabel id="select-label">市区町村</InputLabel>
                        <Select
                          labelId="select-label"
                          label="市区町村"
                          value={city}
                          onChange={handleCity}
                        >
                          {getCityMenuItems(cityList)}
                        </Select>
                        <FormHelperText>
                          {validator.city.state.helperTextWhenError}
                        </FormHelperText>
                      </FormControl>
                    </SizingWrapper>
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* 検索条件 */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ my: 2 }}>
                  <Label>検索条件</Label>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="search-condition"
                      name="search-condition"
                      value={startsWithMatch ? "startsWithMatch" : "exactMatch"}
                      onChange={handleSearchConditionChange}
                    >
                      <FormControlLabel
                        value="exactMatch"
                        control={<Radio />}
                        label="完全一致"
                        disabled={commercialBookTypeCompanyNumber}
                      />
                      <FormControlLabel
                        value="startsWithMatch"
                        control={<Radio />}
                        label="前方一致"
                        disabled={commercialBookTypeCompanyNumber}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Label required={commercialBookTypeCompanyName}>
                    商号・名称
                  </Label>
                </Box>
                <TextField
                  error={validator.commercialBookName.state.error}
                  label="商号・名称（40文字）"
                  helperText={
                    validator.commercialBookName.state.error
                      ? validator.commercialBookName.state.helperTextWhenError
                      : "会社法人種別を除いて40文字以内で入力してください。（例：株式会社法務建設を検索する場合 法務建設）"
                  }
                  value={commercialBookName}
                  inputProps={{ maxLength: 40 }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const inputCommercialBookName =
                      event.target.value.length <= 40
                        ? event.target.value
                        : event.target.value.slice(0, 40);
                    setStateCommercialBookName(inputCommercialBookName);
                  }}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    if (commercialBookTypeCompanyNumber) {
                      return;
                    }
                    // 「株式会社」を含む場合は削除する
                    const inputCommercialBookName = event.target.value
                      .replace(/^(株式会社)/g, "")
                      .replace(/(株式会社)$/g, "");
                    setStateCommercialBookName(inputCommercialBookName);
                    validator.commercialBookName.validate(
                      inputCommercialBookName
                    );
                  }}
                  sx={{ width: { xs: "100%", sm: "700px" } }}
                  disabled={commercialBookTypeCompanyNumber}
                />
              </Grid>
            </Grid>

            {/* 会社番号 */}
            <Grid container spacing={2}>
              <Grid item>
                <Box sx={{ mt: 2 }}>
                  <Label required={commercialBookTypeCompanyNumber}>
                    会社法人等番号
                  </Label>
                </Box>
              </Grid>
            </Grid>
            <Grid container sx={{ alignItems: "center", rowGap: 1 }}>
              <Grid item>
                <TextField
                  error={validator.commercialBookNumberPart1.state.error}
                  label="会社法人等番号（4桁）"
                  helperText={
                    validator.commercialBookNumberPart1.state
                      .helperTextWhenError
                  }
                  value={commercialBookNumberPart1}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (!/^[0-9０-９]*$/.test(event.target.value)) {
                      return;
                    }
                    setCommercialBookNumberPart1(event.target.value);
                  }}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    if (commercialBookTypeCompanyName) {
                      return;
                    }
                    validator.commercialBookNumberPart1.validate(
                      event.target.value
                    );
                  }}
                  inputProps={{ maxLength: 4 }}
                  sx={{ width: "auto" }}
                  disabled={commercialBookTypeCompanyName}
                />
              </Grid>
              <Typography sx={{ margin: "auto 10px" }}>ー</Typography>
              <Grid item>
                <TextField
                  error={validator.commercialBookNumberPart2.state.error}
                  label="（2桁）"
                  helperText={
                    validator.commercialBookNumberPart2.state
                      .helperTextWhenError
                  }
                  value={commercialBookNumberPart2}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (!/^[0-9０-９]*$/.test(event.target.value)) {
                      return;
                    }
                    setCommercialBookNumberPart2(event.target.value);
                  }}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    validator.commercialBookNumberPart2.validate(
                      event.target.value
                    );
                  }}
                  inputProps={{ maxLength: 2 }}
                  sx={{ width: "auto" }}
                  disabled={commercialBookTypeCompanyName}
                />
              </Grid>
              <Typography sx={{ margin: "auto 10px" }}>ー</Typography>
              <Grid item>
                <TextField
                  error={validator.commercialBookNumberPart3.state.error}
                  label="（6桁）"
                  helperText={
                    validator.commercialBookNumberPart3.state
                      .helperTextWhenError
                  }
                  value={commercialBookNumberPart3}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (!/^[0-9０-９]*$/.test(event.target.value)) {
                      return;
                    }
                    setCommercialBookNumberPart3(event.target.value);
                  }}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    validator.commercialBookNumberPart3.validate(
                      event.target.value
                    );
                  }}
                  inputProps={{ maxLength: 6 }}
                  sx={{ width: "auto" }}
                  disabled={commercialBookTypeCompanyName}
                />
              </Grid>
              <Grid item sx={{ display: "flex" }}>
                <Typography
                  sx={{ color: "text.secondary", margin: "auto 10px" }}
                >
                  12桁(4桁-2桁-6桁)を入力してください（全角又は半角数字のみ）
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* 取得ボタン */}
          <Box
            sx={{
              mr: isMobile ? 0 : 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              label="検索"
              onClick={() => {
                handleCheckValidation();
              }}
              variant={ButtonVariantOption.Outlined}
              size={SizingWrapperStyle.SMALL}
            />
          </Box>
        </SectionWrapper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </Box>
    </PagePaper>
  );
};
